/// <amd-module name="TFS/Admin/Views/ProfileSettings/OrganizationsView/OrganizationsView" />
import React = require("react");

import { IVssComponentContext, VssComponent, Components } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Header, TitleSize, HeaderDescription } from "VSSUI/Header";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { Observer } from "VSSUI/Observer";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { ObservableValue, ObservableArray } from "VSS/Core/Observable";
import { localeFormat } from "VSS/Core/Util/String";

import * as Resources from "TFS/Admin/Views/ProfileSettings/OrganizationsView/Resources";
import OrganizationsTableView from "TFS/Admin/Views/ProfileSettings/OrganizationsView/Components/OrganizationsTableView";
import ConfirmationDialog from "TFS/Admin/Views/ProfileSettings/OrganizationsView/Components/ConfirmationDialog";
import { Organization } from "TFS/Admin/Views/ProfileSettings/OrganizationsView/Contracts";
import { AdminTelemetry, AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { sendToast } from "TFS/Admin/Common/Util/SendToastUtility";
import { CleanErrorMessage } from "TFS/Admin/Common/Util/CustomErrorHelper";

import { IVssContributionService } from "VSS/Platform/Contribution";

class OrganizationsComponent extends VssComponent<{}, {}> {
    private readonly tokenProvider = "ms.vss-admin-web.organization-admin-overview-token-data-provider";
    private readonly orgProvider = "ms.vss-admin-web.profile-organizations-view-data-provider";
    private readonly leaveProvider = "ms.vss-admin-web.profile-organizations-leave-data-provider";
    private readonly deletedProvider = "ms.vss-admin-web.profile-organizations-deleted-data-provider";
    private readonly restoreProvider = "ms.vss-admin-web.profile-organizations-restore-data-provider";

    private _telemetryLogger = new AdminTelemetry(this.context.pageContext);
    private _currentOrgs = new ObservableArray<Organization>();
    private _deletedOrgs = new ObservableArray<Organization>();
    private _showLeaveDialog = new ObservableValue<boolean>(false);
    private _showRestoreDialog = new ObservableValue<boolean>(false);
    private _errorMessage = new ObservableValue<string>("");
    private _selectedOrg?: Organization;
    private _isAadUser = false;

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);
    }

    public componentDidMount() {
        this.initialFetch();
    }

    public render() {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow rhythm-vertical-16">
                    <Header title={Resources.OrganizationsTitle} titleSize={TitleSize.Large} />
                    <HeaderDescription className="org-description">
                        {Resources.TitleDescription}
                    </HeaderDescription>

                    <Observer message={this._errorMessage}>
                        {(props: { message: string }) => props.message}
                    </Observer>

                    <OrganizationsTableView
                        organizations={this._currentOrgs}
                        onButtonClicked={this.onShowLeaveDialog}
                    />
                    <OrganizationsTableView
                        organizations={this._deletedOrgs}
                        onButtonClicked={this.onShowRestoreDialog}
                        deletedOrgs={true}
                    />
                    
                    <Observer showDialog={this._showLeaveDialog}>
                        {(props: { showDialog: boolean }) => 
                            props.showDialog &&
                            <ConfirmationDialog 
                                onConfirm={this.onLeaveOrg}
                                onDismiss={this.onDismissLeaveDialog}
                                selectedOrg={this._selectedOrg!}
                                isAadUser={this._isAadUser}
                            />
                        }
                    </Observer>
                    <Observer showDialog={this._showRestoreDialog}>
                        {(props: { showDialog: boolean }) => 
                            props.showDialog &&
                            <ConfirmationDialog 
                                onConfirm={this.onRestoreOrg}
                                onDismiss={this.onDismissRestoreDialog}
                                selectedOrg={this._selectedOrg!}
                                isAadUser={this._isAadUser}
                                isDeleted={true}
                            />
                        }
                    </Observer>
                </Page>
            </Surface>
        );
    }
                                                                                                                                                                                                                                                                                                                               
    private initialFetch = async () => {
        const telemetryLogger = this._startActionCapture("Fetch organizations");
        const { pageContext } = this.context;
        try {
            const service = pageContext.getService<IVssContributionService>("IVssContributionService");
            const data = await service.getDataAsync<{ [id: string]: any }>(this.orgProvider);
            
            if (!data) {
                throw new Error("Failed to fetch user organizations");
            }

            for (const organization of data!.ownerAccounts) {
                this._currentOrgs.push({
                    ...organization,
                    isOwner: true,
                    isActive: true
                })
            }
            for (const organization of data!.memberAccounts) {
                this._currentOrgs.push({
                    ...organization,
                    isOwner: false,
                    isActive: true
                })
            }
            this._isAadUser = !!data.isAadUser;
            this._markTimeToInteractive();

            this.fetchDeletedOrgs(data.ownerDeletedAccountIds, data.memberDeletedAccountIds);
        } catch (error) {
            telemetryLogger.captureError(error);
            this.onShowErrorMessage(error);
        }
    };

    private fetchDeletedOrgs = async (ownerIds: string[], memberIds: string[]) => {
        const telemetryLogger = this._startActionCapture("Fetch deleted organizations");
        const { pageContext } = this.context;
        try {
            const service = pageContext.getService<IVssContributionService>("IVssContributionService");
            const owner = await Promise.all(ownerIds.map(async accountId => {
                let data = await service.getDataAsync<Organization>(this.deletedProvider, { accountId });
                return data && {
                    ...data,
                    isOwner: true,
                    isActive: false
                } as Organization;
            }));
            const member = await Promise.all(memberIds.map(async accountId => {
                const data = await service.getDataAsync<Organization>(this.deletedProvider, { accountId });
                return {
                    ...data,
                    isOwner: false,
                    isActive: false
                } as Organization;
            }));
            this._deletedOrgs.push(...(owner.filter(x => !!x) as Organization[]), ...member.filter(x => !!x));
        } catch (error) {
            telemetryLogger.captureError(error);
            this.onShowErrorMessage(error);
        }
    }

    private onLeaveOrg = async (organization: Organization) => {
        const telemetryLogger = this._startActionCapture("Leave organization");
        const { pageContext } = this.context;
        try {
            const service = pageContext.getService<IVssContributionService>("IVssContributionService");
            const result = await service.getDataAsync<{ [id: string]: any }>(this.leaveProvider, { accountId: organization.id });
            this._errorMessage.value = "";
            sendToast(localeFormat(Resources.LeaveSuccess, organization.name), pageContext);
        } catch (error) {
            telemetryLogger.captureError(error);
            this.onShowErrorMessage(error, Resources.LeaveFailed, organization.name);
        } finally {
            this.onDismissLeaveDialog();
        }
    };

    private onRestoreOrg = async (organization: Organization, newName: string) => {
        const telemetryLogger = this._startActionCapture("Restore organization");
        const { pageContext } = this.context;
        try {
            const service = pageContext.getService<IVssContributionService>("IVssContributionService");
            const result = await service.getDataAsync<boolean>(this.restoreProvider,
                {
                    accountId: organization.id,
                    newName: newName || organization.name
                }
            );
            if (!result) {
                throw new Error("An error ocurred while trying to restore the organization");
            }

            this._deletedOrgs.splice(this._deletedOrgs.value.findIndex(x => x.id === organization.id), 1);
            this._currentOrgs.push({
                ...organization,
                isActive: true,
            });
            this._errorMessage.value = "";
            sendToast(localeFormat(Resources.RestoreSuccess, organization.name), pageContext);
        } catch (error) {
            telemetryLogger.captureError(error);
            this.onShowErrorMessage(error, Resources.RestoreFailed, organization.name);
        } finally {
            this.onDismissRestoreDialog();
        }
    };

    private onShowLeaveDialog = (organization: Organization) => {
        this._selectedOrg = organization;
        this._showLeaveDialog.value = true;
    }

    private onDismissLeaveDialog = () => {
        this._showLeaveDialog.value = false;
    }

    private onShowRestoreDialog = (organization: Organization) => {
        this._selectedOrg = organization;
        this._showRestoreDialog.value = true;
    }

    private onDismissRestoreDialog = () => {
        this._showRestoreDialog.value = false;
    }

    private onShowErrorMessage(error: any, title = "", ...args: any[]) {
        const message = CleanErrorMessage((error.serverError && error.serverError.message) || error.serverError || error.message);
        this._errorMessage.value = this.getMessageCard(`${title}${title && `: `}${message}`, ...args) as unknown as string;
    }

    private getMessageCard(message: string, ...args: any[]) {
        return (
            <MessageCard
                className="org-card"
                severity={MessageCardSeverity.Error}
                onDismiss={() => this._errorMessage.value = ""}
            >
                <FormatComponent format={message}>
                    {args.map((x, i) => <b key={`key-${i}`}>{x}</b>)}
                </FormatComponent>
            </MessageCard>
        );
    }

    private _startActionCapture(action: string, properties: { [key: string]: any } = {}): AdminFeatureTelemetryLogger {
        return this._telemetryLogger.startActionCapture(Feature.ProfileOrganizations, Feature.ProfileOrganizations, action, properties);
    }

    private _markTimeToInteractive() {
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(Area.Admin, Feature.ProfileOrganizations);
    }
}

Components.add("ms.vss-admin-web.profile-settings-organizations-view-component", OrganizationsComponent);
